import { createFileRoute } from '@tanstack/react-router';
import DOMPurify from 'isomorphic-dompurify';
import { useMemo } from 'react';

import ProductCard from '../components/ProductCard';
import { JsonLdProducts } from '../components/SEO';
import { useGetContent } from '../lib/react-query/ContentQueries';
import { useGetAllProducts } from '../lib/react-query/ProductQueries';
import type { ProductWithCategory } from '../types/product.types';

export const Route = createFileRoute('/prix')({
  component: PricePage,
  head: () => ({
    meta: [
      {
        title: 'Prix | Azelysse Piercing & Tattoo',
      },
      {
        name: 'description',
        content:
          "Tarifs piercing et tatouage chez Azelysse à Égly (91). Prix transparents, bijoux en titane ASTM F-136. Proche d'Arpajon, Brétigny-sur-Orge, Montlhéry.",
      },
      {
        property: 'og:title',
        content: 'Prix | Azelysse Piercing & Tattoo',
      },
      {
        property: 'og:description',
        content:
          "Tarifs piercing et tatouage chez Azelysse à Égly (91). Prix transparents, bijoux en titane ASTM F-136. Proche d'Arpajon, Brétigny, Montlhéry.",
      },
      {
        property: 'og:url',
        content: 'https://azelysse.fr/prix',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://azelysse.fr/prix' }],
  }),
});

function PricePage() {
  const { data: products, isLoading } = useGetAllProducts();
  const { data: content } = useGetContent();

  const productsByCategory = useMemo(() => {
    if (!products) return {};

    return products.reduce(
      (acc, product) => {
        // Ensure the category exists and has a name before adding to accumulator
        const categoryName = (product.category && product.category.name) || 'Uncategorized';
        // Initialize the category array if it does not exist
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!acc[categoryName]) {
          acc[categoryName] = [];
        }
        // Add the product to the category
        acc[categoryName].push(product);
        return acc;
      },
      {} as Record<string, Array<ProductWithCategory>>,
    );
  }, [products]);

  if (isLoading || !products) {
    return (
      <div className="mx-auto max-w-7xl px-6 pt-24 pb-16 lg:px-8">
        <section className="mb-16">
          <h1 className="mb-8 text-4xl font-semibold text-gray-900">Prix</h1>
          <p className="text-gray-600">Chargement des produits...</p>
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 pt-24 pb-16 lg:px-8">
      <JsonLdProducts products={products} />
      <section className="mb-16">
        <h1 className="mb-8 text-4xl font-semibold text-gray-900">Prix</h1>

        {content?.content_page?.price_page_section && (
          <section
            className="content-page mb-8"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(content.content_page.price_page_section),
            }}
          />
        )}
      </section>

      {Object.entries(productsByCategory).map(([category, categoryProducts]) => {
        return (
          <div key={category} className="mb-16">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">{category}</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
              {categoryProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
